<template>
    <div>
        <h3 class="title mb-3">{{$t('page-unit-manual-booking.title')}}</h3>
        <p>{{$t('page-unit-manual-booking.intro')}}</p>
        <v-divider class="my-6"></v-divider>
        <h3 class="title mb-3">{{$t('page-unit-manual-booking.section-make-dates-unavailable.title')}}</h3>
        <v-form ref="form" v-model="valid">
            <v-row>
                <v-col class="py-0">
                    <v-checkbox
                        v-model="noDatesToBlock"
                        :label="$t('page-unit-manual-booking.section-make-dates-unavailable.fields.no-dates-to-block.label')"
                    ></v-checkbox>
                </v-col>
            </v-row>
            <div v-if="!noDatesToBlock">
                <v-row>
                    <v-col>
                        <v-text-field
                        v-model="label"
                        :label="$t('page-unit-manual-booking.section-make-dates-unavailable.fields.reason.label')"
                        outlined
                        :hint="$t('page-unit-manual-booking.section-make-dates-unavailable.fields.reason.hint')"
                        persistent-hint
                        :rules="labelRules"
                        ></v-text-field>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col>
                        <v-menu
                        ref="fromDateMenu"
                        v-model="fromDateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    outlined
                                    :value="fromDateFormatted"
                                    :label="$t('page-unit-manual-booking.section-make-dates-unavailable.fields.from-date.label')"
                                    append-icon="mdi-calendar"
                                    v-on="on"
                                    clearable
                                    :rules="fromDateRules"
                                    @keydown.stop.prevent
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="fromDate"
                                no-title
                                @input="selectFromDate()"
                                :min="moment(new Date()).format('YYYY-MM-DD')"
                                :allowedDates="allowedDates"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col>
                        <v-menu
                        ref="toDateMenu"
                        v-model="toDateMenu"
                        :close-on-content-click="false"
                        transition="scale-transition"
                        offset-y
                        max-width="290px"
                        min-width="290px"
                        >
                            <template v-slot:activator="{ on }">
                                <v-text-field
                                    :disabled="!fromDate"
                                    outlined
                                    :value="toDateFormatted"
                                    :label="$t('page-unit-manual-booking.section-make-dates-unavailable.fields.to-date.label')"
                                    append-icon="mdi-calendar"
                                    v-on="on"
                                    clearable
                                    :rules="toDateRules"
                                    @keydown.stop.prevent
                                ></v-text-field>
                            </template>
                            <v-date-picker
                                v-model="toDate"
                                no-title
                                @input="toDateMenu = false"
                                :min="moment(fromDate).add(1, 'd').format('YYYY-MM-DD')"
                                :picker-date.sync="toPickerDate"
                            ></v-date-picker>
                        </v-menu>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="6" class="py-0">
                        <v-select
                            v-model="category"
                            :items="categoryItems"
                            :label="$t('page-unit-manual-booking.section-make-dates-unavailable.fields.unavailable-status.label')"
                            item-value="key"
                            outlined
                            :rules="categoryRules"
                        ></v-select>
                    </v-col>
                </v-row>
            </div>
            <v-row>
                <v-col>
                    <v-btn large color="primary" @click="createBooking()" :loading="applyingBooking">{{$t('global-terms.apply')}}</v-btn>
                </v-col>
            </v-row>
        </v-form>

        <v-divider class="my-6"></v-divider>

        <v-row>
            <v-col>
                <h3 class="title mb-3">{{$t('page-unit-manual-booking.section-added-by-you.title')}}</h3>
            </v-col>
            <v-col class="text-right">
                <v-btn outlined color="primary" small v-if="udSelected != 'LIST'" @click="switchUD('LIST')">
                    <v-icon small left>mdi-format-list-checkbox</v-icon>
                    {{$t('global-terms.list')}}
                </v-btn>
                <v-btn outlined color="primary" small v-else-if="udSelected != 'CALENDAR'" @click="switchUD('CALENDAR')">
                    <v-icon small left>mdi-calendar</v-icon>
                    {{$t('global-terms.calendar')}}
                </v-btn>
            </v-col>
        </v-row>

        <div v-if="udSelected === 'LIST'">
            <h4 class="subtitle-1">{{$t('page-unit-manual-booking.section-added-by-you.header')}}</h4>
            <booking-added-by-you-table :bookings="internalBookings" :unit="unit"></booking-added-by-you-table>
            <h4 class="subtitle-1">{{$t('page-unit-manual-booking.section-other-channels.header')}}</h4>
            <booking-other-channels-table :bookings="externalBookings" :unit="unit"></booking-other-channels-table>
        </div>
        <div v-else-if="udSelected === 'CALENDAR'">
            <v-date-picker
                v-model="flattenedBookingDates"
                no-title
                multiple
                readonly
            ></v-date-picker>
        </div>
    </div>
</template>
<script>
import { forEachObjIndexed, clone } from 'ramda'
import moment from 'moment'
import { mapState, mapActions } from 'pinia'
import BookingAddedByYouTable from './BookingAddedByYouTable'
import BookingOtherChannelsTable from './BookingOtherChannelsTable'
import { UNAVAILABLE_STATUSES } from '../../../../../constants'
import { useBookingStore } from '@/stores'

export default {
  name: 'manage-manually-tab',
  data () {
    return {
      applyingBooking: false,
      valid: true,
      labelRules: [
        v => (v && v.length <= 100) || this.$t('page-unit-manual-booking.section-make-dates-unavailable.fields.reason.validation-length'),
        v => !!v || this.$t('page-unit-manual-booking.section-make-dates-unavailable.fields.reason.validation-required'),
      ],
      fromDateRules: [
        v => moment(v, 'DD/MM/Y').isValid() || this.$t('page-unit-manual-booking.section-make-dates-unavailable.fields.from-date.validation-date'),
        v => !!v || this.$t('page-unit-manual-booking.section-make-dates-unavailable.fields.from-date.validation-required'),
      ],
      toDateRules: [
        v => moment(v, 'DD/MM/Y').isValid() || this.$t('page-unit-manual-booking.section-make-dates-unavailable.fields.to-date.validation-date'),
        v => !!v || this.$t('page-unit-manual-booking.section-make-dates-unavailable.fields.to-date.validation-required'),
      ],
      categoryRules: [
        v => !!v || this.$t('page-unit-manual-booking.section-make-dates-unavailable.fields.unavailable-status.validation-required'),
      ],
      label: '',
      udSelected: 'LIST',
      noDatesToBlock: false,
      category: 0,
      fromDate: null,
      toDate: null,
      fromDateMenu: false,
      toDateMenu: false,
      toPickerDate: null,
      unavailableDateCalendar: null,
    }
  },
  props: {
    unit: {
      type: Object,
      required: true,
    },
    place: {
      type: Object,
      required: true,
    },
  },
  methods: {
    clone,
    ...mapActions(useBookingStore, [
      'httpCreateBooking',
      'httpGetBookingsByUnit',
    ]),
    switchUD (type) {
      this.udSelected = type

      if (type === 'CALENDAR') {
        this.tagManager.addEvent('Manage Calendar', 'View Calendar', this.place.name)
      }
    },
    createBooking () {
      if (this.$refs.form.validate()) {
        this.applyingBooking = true

        let booking = {
          unit: this.unit,
          label: this.label,
          noDatesToBlock: this.noDatesToBlock,
          category: this.category,
          ref: this.unit.syncing === true ? '' : 'REAL-SAWDAYS', // TODO: propsync - can just set this to REAL-SAWDAYS when propsync is removed
        }

        if (!this.noDatesToBlock) {
          booking.dates = {
            start: this.fromDate,
            end: this.toDate,
          }
        }

        const cloneUnit = this.clone(this.unit)
        this.httpCreateBooking({ booking, unit: cloneUnit })
          .then(() => {
            this.$refs.form.reset()
            this.applyingBooking = false
            this.noDatesToBlock = false

            this.tagManager.addEvent('Manage Calendar', 'Apply Unavailable Dates', this.place.name)
          })
      }
    },
    allowedDates (date) {
      // disable any date that is currently booked
      return !this.flattenedBookingDates.includes(date)
    },
    selectFromDate () {
      this.fromDateMenu = false
      this.toPickerDate = this.fromDate
    },
  },
  computed: {
    ...mapState(useBookingStore, {
      bookingsByIds: 'findByIds',
      internalBookingsByIds: 'findInternalByIds',
      externalBookingsByIds: 'findExternalByIds',
    }),
    categoryItems () {
      let categories = []
      forEachObjIndexed((value, key) => {
        const status = {
          key: key,
          text: this.$t(`page-unit-manual-booking.section-make-dates-unavailable.fields.unavailable-status.values.${key}`),
        }
        categories.push(status)
      })(UNAVAILABLE_STATUSES)

      return categories
    },
    bookings () {
      return this.bookingsByIds(this.unit.bookings)
    },
    internalBookings () {
      return this.internalBookingsByIds(this.unit.bookings)
    },
    externalBookings () {
      return this.externalBookingsByIds(this.unit.bookings)
    },
    flattenedBookingDates () {
      let flattenedDates = []

      forEachObjIndexed((booking) => {
        booking.dates.each.forEach((date) => {
          flattenedDates.push(date)
        })
      })(this.bookings)

      return flattenedDates
    },
    fromDateFormatted () {
      return this.fromDate ? moment(this.fromDate).format('DD/MM/Y') : ''
    },
    toDateFormatted () {
      return this.toDate ? moment(this.toDate).format('DD/MM/Y') : ''
    },
  },
  components: {
    BookingAddedByYouTable,
    BookingOtherChannelsTable,
  },
  created () {
    this.tagManager.addEvent('Manage Calendar', 'Manual Calendar', this.place.name)
  },
  mounted () {
    const cloneUnit = this.clone(this.unit)
    this.httpGetBookingsByUnit({ unit: cloneUnit })
  },
}
</script>
